import { Event, PREVIEW_ID_ATTRIBUTE, CUSTOM_PREVIEW_ID_PREFIX, Action } from './_consts.js';
import { $create, $$, $Filter } from './$utils.js';
import DomObserver from './_dom-observer.js';


const renderButtons = async ({ container, buttons, scope, hide }) => {
  const visibles = await Promise.all(buttons.map(async (button) => {
    const $button = container.appendChild($create(`span.tpp-button[disabled]`));
    if (await button.isVisible(scope)) {
      scope = Object.assign({}, scope, { $button });

      const _execute = (item = undefined) => async (e) => {
        e.stopPropagation();
        hide();
        button.beforeExecute(scope, item);
        try {
          const result = await button.execute(scope, item);
          button.afterExecute(scope, item, result);
        } catch (e) {
          console.error(e);
          button.afterExecute(scope, item, null, e);
        }
      };

      button.getIcon(scope);
      Promise.resolve(button.isEnabled(scope)).then(enabled => enabled === true && $button.removeAttribute('disabled'));
      Promise.resolve(button.getLabel(scope)).then(label => label && ($button.title = label));
      $button.addEventListener('click', _execute());

      Promise.resolve(button.getItems(scope)).then(items => {
        if (items instanceof Array && items.length !== 0) {
          const $ul = $button.appendChild($create('ul'));
          items.forEach(item => {
            const $li = $ul.appendChild($create('li'));
            $li.innerText = item.label || item;
            $li.addEventListener('click', _execute(item));
          });
        }
      });

      return true;
    } else {
      $button.parentNode.removeChild($button);
    }
  }));

  return visibles.some(isVisible => isVisible === true);
};


const DecoratedElementSymbol = Symbol('DecoratedElementSymbol');
const ElementStatusSymbol = Symbol('ElementStatusSymbol');

export default function Decoration({ events, messenger, actions, preview }) {
  let $container = null;

  class DecoratedElement {
    constructor(node, previewId) {
      this.$node = node;
      this.$node.decoration = this;
      this._previewId = previewId;

      this.$borders = $container.appendChild($create('.tpp-borders'));
      this.$buttons = this.$borders.appendChild($create('.tpp-buttons'));

      actions.getElementStatus(previewId).then(({ elementType }) => {
        if (elementType === 'Media') {
          this.$buttons.classList.add('bottom');
        }
      });

      this._isVisible = false;
      this._intersectionChangeHandler = this.showButtons.bind(this);

      let _timer = null;
      [this.$node, this.$buttons].forEach($el => {
        $el.addEventListener('mouseenter', () => clearTimeout(_timer) || this.showButtons());
        $el.addEventListener('mouseleave', () => _timer = setTimeout(this.hideButtons.bind(this), 10));
      });
    }
    async showButtons() {
      const { top, left, width, height } = this.$node.getBoundingClientRect();
      this.$borders.style.cssText = `opacity:1;top:${top}px;left:${left}px;width:${width}px;height:${Math.max(height, 27)}px`;

      if (!this._isVisible) {
        this._isVisible = true;

        window.addEventListener('scroll', this._intersectionChangeHandler);
        window.addEventListener('resize', this._intersectionChangeHandler);

        this.$buttons.innerHTML = '';

        if (typeof this.$node[ElementStatusSymbol] === 'undefined') {
          this.$node[ElementStatusSymbol] = await actions.getElementStatus(this._previewId);
        }
        const status = this.$node[ElementStatusSymbol];
        const language = await actions.getPreviewLanguage();
        const scope = { $node: this.$node, previewId: this._previewId, status, language };
        const hasButtons = await renderButtons({ container: this.$buttons, buttons: preview._buttons, scope, hide: () => this.hideButtons() });
        if (!hasButtons) {
          this.$borders.style.cssText = 'transition: all 0s';
          window.removeEventListener('scroll', this._intersectionChangeHandler);
          window.removeEventListener('resize', this._intersectionChangeHandler);
        }
      }
    }
    hideButtons() {
      if (this._isVisible) {
        this._isVisible = false;
        window.removeEventListener('scroll', this._intersectionChangeHandler);
        window.removeEventListener('resize', this._intersectionChangeHandler);
        this.$borders.style.cssText = 'transition: all 0s';
      }
    }
    undecorate() {
      delete this.$node.decoration;
      delete this.$node[DecoratedElementSymbol];
      window.removeEventListener('scroll', this._intersectionChangeHandler);
      window.removeEventListener('resize', this._intersectionChangeHandler);
      $container.removeChild(this.$borders);
    }
  }


  events.on(Event.Initialized, () => {
    $container = document.body.appendChild($create('.tpp-borders-container'));

    DomObserver({
      onInsert: (node, previewId) => {
        if (!(node[DecoratedElementSymbol] instanceof DecoratedElement)) {
          node[DecoratedElementSymbol] = new DecoratedElement(node, previewId);
        }
      },
      onRemove: (node) => {
        if (node[DecoratedElementSymbol] instanceof DecoratedElement) {
          node[DecoratedElementSymbol].undecorate();
        }
      }
    });
  });

  const findPreviewNodes = (previewId, neverReturnEmptyList = false) => {
    const list = $$(`[${PREVIEW_ID_ATTRIBUTE}="${previewId}"]`);
    return neverReturnEmptyList && list.length === 0 ? [null] : list;
  }
  events.on(Event.StatusChange, ({ previewId, status }) => findPreviewNodes(previewId)
    .forEach($node => $node[ElementStatusSymbol] = status)
  );

  const _previewElementNode = async(previewIdOrNode) => {
    let previewElementNode = previewIdOrNode instanceof HTMLElement && previewIdOrNode.matches(`[${PREVIEW_ID_ATTRIBUTE}]`)
      ? { previewId: previewIdOrNode.getAttribute(PREVIEW_ID_ATTRIBUTE), $node: previewIdOrNode }
      : { previewId: previewIdOrNode, $node: findPreviewNodes(previewElementNode)[0] || null };
    return Object.assign(previewElementNode, await actions.getElementStatus(previewElementNode.previewId));
  };
  const _previewElementNodes = async() => Promise.all($$(`[${PREVIEW_ID_ATTRIBUTE}]`).map(_previewElementNode));

  Object.assign(actions, {
    findPreviewNodes,
    _previewElementNode,
    _previewElementNodes,
  });
}
